import React, { useEffect, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetAuthDetails } from '../../hooks/getAuthDetails';
import { SLICES } from '../../constants/slices';
import { CSPUrlsMap } from '../../routes/CSPUrlsMap';
import { BrandContext } from '../../contextProviders/brandContext';
import { useLazyGetPolicyVersionsQuery } from '../../data/policy';
import { GLM_PRODUCT_VERSION, AUTHENTICATION_TYPES } from '../../constants/constants';
import Loading from '../../components/shared/Loading/Loading';
import { FeatureFlagContext } from '../../contextProviders/featureFlagContext';
import useStepupChallenge from '../../hooks/useStepupChallenge';

const Verification = () => {
  const {
    activeBrandContext: {
      cspConfiguration: {
        auth0Config: {
          [process.env.REACT_APP_ENV]: { gen2CspUrl, authenticationType }
        }
      }
    }
  } = useContext(BrandContext);
  const { stepUpSmsChallenge } = useStepupChallenge();
  const { cspConfiguration, isCspConfigLoading } = useContext(FeatureFlagContext);
  const isLiteModeEnabled = cspConfiguration?.IsLiteModeEnabled;
  const isDualPortal = cspConfiguration?.IsDualPortal;
  const { isAuth0Authenticated, auth0AccessToken } = useGetAuthDetails();
  const navigate = useNavigate();
  const { tokenClaims, authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const [getPolicyGlmVersion] = useLazyGetPolicyVersionsQuery();
  const isSSOAuthentication = authenticationType === AUTHENTICATION_TYPES.SSO;

  const redirectTo = useRef(null);

  const urlParams = new URLSearchParams(window.location.search);
  if (!redirectTo.current) redirectTo.current = urlParams.get('redirect_to');

  useEffect(() => {
    if (!cspConfiguration || isCspConfigLoading) {
      return;
    }

    if (isAuth0Authenticated && isLiteModeEnabled) {
      navigate(CSPUrlsMap.dashboard.url, { replace: true });
    }
    if (authDetails?.cspAccessToken && auth0AccessToken) {
      const isAuthenticated = isAuth0Authenticated && authDetails?.cspAccessToken;

      const handleGlm3Redirection = () => {
        const showVerificationEmail =
          tokenClaims?.registrationInProgress &&
          tokenClaims?.verificationPendingEmail &&
          isAuthenticated &&
          !tokenClaims?.mfaSkip;

        const showVerificationSms =
          (isAuthenticated &&
            tokenClaims?.registrationInProgress &&
            tokenClaims?.verificationPendingPhone &&
            !tokenClaims?.verificationPendingEmail &&
            !tokenClaims?.mfaSkip) ||
          (isAuthenticated &&
            tokenClaims?.registrationInProgress &&
            !tokenClaims?.verificationPendingPhone &&
            !tokenClaims?.verificationPendingEmail &&
            !tokenClaims?.mfaSkip) ||
          (isAuthenticated &&
            !tokenClaims?.registrationInProgress &&
            tokenClaims?.verificationPendingPhone &&
            !tokenClaims?.verificationPendingEmail &&
            !tokenClaims?.mfaSkip) ||
          (tokenClaims?.registrationInProgress && tokenClaims?.migratedUser);

        const isMfaSkip = isAuthenticated && !tokenClaims?.registrationInProgress && tokenClaims?.mfaSkip;

        const showDashboardPage = isSSOAuthentication
          ? isAuthenticated
          : (!tokenClaims?.verificationPendingEmail &&
              !tokenClaims?.verificationPendingPhone &&
              !tokenClaims?.registrationInProgress &&
              isAuthenticated) ||
            isMfaSkip ||
            (!tokenClaims?.registrationInProgress && tokenClaims?.migratedUser);

        if (showDashboardPage) {
          if (redirectTo.current && !isSSOAuthentication) {
            navigate(redirectTo.current, { replace: true });
          } else {
            navigate(CSPUrlsMap.dashboard.url, { replace: true });
          }
        } else if (showVerificationSms) {
          stepUpSmsChallenge(`${window.location.origin}${CSPUrlsMap.verifySms.url}`);
        } else if (showVerificationEmail) {
          stepUpSmsChallenge(`${window.location.origin}${CSPUrlsMap.verifyEmail.url}`);
        }
      };

      const handleGlm2Redirection = async () => {
        let redirected = false;
        try {
          const result = await getPolicyGlmVersion(auth0AccessToken).unwrap();

          const isGlmTwoVersion = (policies) => {
            return (
              Array.isArray(policies) &&
              policies.length > 0 &&
              policies.every((policy) => policy.ProductVersion === GLM_PRODUCT_VERSION.GLM2)
            );
          };

          const isGlmTwoVersionPolicy = isGlmTwoVersion(result);
          const showGen2Portal = isDualPortal && isGlmTwoVersionPolicy && !isLiteModeEnabled && !isCspConfigLoading;

          if (showGen2Portal) {
            window.location.href = gen2CspUrl;
            redirected = true;
            return;
          }
        } catch (error) {
          handleGlm3Redirection();
        }

        // fallback logic if 2.5 redirection is not triggered
        if (!redirected && !isCspConfigLoading) {
          handleGlm3Redirection();
        }
      };

      handleGlm2Redirection();
    }

    // TODO: Else redirect to error page here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authDetails?.cspAccessToken, auth0AccessToken, isCspConfigLoading]);
  return <Loading />;
};

export default Verification;
